import React, { lazy, Suspense, useEffect } from 'react'
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom'
import GLayout from './component/GLayout'
import { getLoggedUsername, getRoles, getUserProfile } from './config/keycloak'
import convert from './component/assets/convert.svg'
import settlement_status from './component/assets/settlement.svg'
import TimerPopup from './component/TimerPopup'
import { useTranslation } from 'react-i18next'
import './i18n'
import './index.css'
import env from './config/env'

const Convert = lazy(() => import('./pages/Convert'))
const Settlement = lazy(() => import('./pages/Settlement'))
const MultiTabError = lazy(() => import('./pages/MultiTabError'))
const PermissionError = lazy(() => import('./pages/PermissionError'))
const ResponsiveInfo = lazy(() => import('./pages/ResponsiveInfo'))

const App: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()

    const rolePermissionList = getRoles()
    const hasSettlementPermission = rolePermissionList?.includes('ROLE_R_SETTLEMENT_STATUS')

    const menuItems: { key: string; label: string; icon: React.ReactNode }[] = [
        {
            key: '/',
            label: t('CONVERT'),
            icon: React.createElement('img', { src: convert, alt: 'logo', className: 'menu-item' }),
        },
    ]

    if (hasSettlementPermission) {
        menuItems.push({
            key: '/settlement',
            label: t('SETTLEMENT_PAGE'),
            icon: React.createElement('img', { src: settlement_status, alt: 'logo', className: 'menu-item menu-item-settlement' }),
        })
    }

    const checkHasViewPermission = () => {
        const rolePermissionList = getRoles()
        if (!rolePermissionList.includes('ROLE_R_ORDER_HISTORY') || !getUserProfile()) {
            navigate('/error')
        }
    }

    const checkHasSettlementPermission = () => {
        if (location.pathname === '/settlement' && !hasSettlementPermission) {
            navigate('/error')
        }
    }

    useEffect(() => {
        document.title = env.keycloakClientId === 'myfx-pro' ? '4XPro' : '4X'
        checkHasViewPermission()
        checkHasSettlementPermission()
    }, [])

    const onMenuClick = ({ key }: { key: string }) => {
        navigate(key)
    }

    const onThemeChange = (theme: string) => {
        localStorage.setItem('theme', theme)
    }

    const handleResize = () => {
        if (window.innerWidth < 481) {
            navigate('/responsive')
        }
    }

    useEffect(() => {
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const RouteLoading = () => {
        return (
            <div className='mt-5'>
                <span className='block w-48 h-12 rounded skeleton'></span>
                <div className='mt-5 border rounded'>
                    <div className='p-5 border-b'>
                        <span className='block w-48 h-8 rounded skeleton'></span>
                    </div>
                    <div className='p-5'>
                        <div className='w-full h-screen skeleton'></div>
                    </div>
                </div>
            </div>
        )
    }

    const selectedKeys = [location.pathname]

    const layoutProps = {
        style: { fontSize: 18 },
        items: menuItems,
        selectedKeys,
        onThemeChange: onThemeChange,
        loginName: getLoggedUsername(),
        defaultTheme: (env.keycloakClientId === 'myfx-pro' ? 'dark' : 'light') as 'dark' | 'light',
        onClick: onMenuClick,
    }

    const isAppRoute = location.pathname === '/' || location.pathname === '/settlement'

    return (
        <>
            {getRoles() === null ? (
                <RouteLoading />
            ) : isAppRoute ? (
                <GLayout {...layoutProps}>
                    <Suspense fallback={<RouteLoading />}>
                        <Routes>
                            <Route
                                path={'/'}
                                element={
                                    <>
                                        <Convert />
                                        <TimerPopup />
                                    </>
                                }
                            />
                            <Route
                                path={'/settlement'}
                                element={
                                    <>
                                        <Settlement />
                                        <TimerPopup />
                                    </>
                                }
                            />
                        </Routes>
                    </Suspense>
                </GLayout>
            ) : (
                <Suspense fallback={<RouteLoading />}>
                    <Routes>
                        <Route path={'/denied'} element={<MultiTabError />} />
                        <Route path={'/error'} element={<PermissionError />} />
                        <Route path={'/responsive'} element={<ResponsiveInfo />} />
                        <Route path='*' element={<PermissionError />} />
                    </Routes>
                </Suspense>
            )}
        </>
    )
}

export default App
